import React, { useState } from 'react';
import styles from './WorkspaceSwitcherMenu.module.css';

/* ── Icons ── */

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.75" />
    <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const ImportIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 3v12m0 0l-3-3m3 3l3-3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
  </svg>
);

/* ── Types ── */

export interface ProjectOption {
  id: string;
  name: string;
  code: string;
  avatarColor?: string;
  active?: boolean;
  onClick: () => void;
}

export interface WorkspaceSwitcherMenuProps {
  projects: ProjectOption[];
  onNewProject?: () => void;
  onImportProject?: () => void;
  onSettings?: () => void;
  onProjectClick?: () => void;
  id?: string;
  style?: React.CSSProperties;
}

/* ── Component ── */

const WorkspaceSwitcherMenu = ({ projects, onNewProject, onImportProject, onSettings, onProjectClick, id, style }: WorkspaceSwitcherMenuProps) => {
  const [search, setSearch] = useState('');

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div id={id} className={styles.menu} style={style} role="dialog" aria-label="Select project">
      {/* Search */}
      <div className={styles.searchWrap}>
        <span className={styles.searchIcon}><SearchIcon /></span>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search project..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      {/* Project list */}
      <ul className={styles.list} role="listbox">
        {filtered.map(project => (
          <li key={project.id} role="option" aria-selected={project.active}>
            <button
              type="button"
              className={[styles.item, project.active ? styles.itemActive : ''].filter(Boolean).join(' ')}
              onClick={() => { project.onClick(); onProjectClick?.(); }}
            >
              <span
                className={styles.avatar}
                style={project.avatarColor ? { background: project.avatarColor } : undefined}
              >
                {project.name.charAt(0).toUpperCase()}
              </span>

              <span className={styles.itemName}>{project.name}</span>

              <span className={styles.itemMeta}>
                <span className={styles.itemCode}>{project.code}</span>
                {project.active && <span className={styles.activeDot} aria-label="projeto ativo" />}
              </span>
            </button>
          </li>
        ))}

        {filtered.length === 0 && (
          <li className={styles.empty}>No projects found.</li>
        )}
      </ul>

      {/* Footer */}
      <div className={styles.footer}>
        {onNewProject && (
          <button type="button" className={styles.footerAction} onClick={onNewProject}>
            <PlusIcon />
            New project
          </button>
        )}
        {onImportProject && (
          <button type="button" className={styles.footerAction} onClick={onImportProject}>
            <ImportIcon />
            Import
          </button>
        )}
        {onSettings && (
          <button type="button" className={styles.footerAction} onClick={onSettings}>
            <SettingsIcon />
            Project settings
          </button>
        )}
      </div>
    </div>
  );
};

WorkspaceSwitcherMenu.displayName = 'WorkspaceSwitcherMenu';

export { WorkspaceSwitcherMenu };
